<script lang="ts">
	import { getDisplayName } from '$lib/perps';
	import type { Order } from '$lib/types/order';
	import { getNumBackInUnits, getDate, getTime, addDollarSign } from '$lib/util/util';
	import BigNumber from 'bignumber.js';
	import { userHoldingActiveItem } from '../store';
	import { getDisplayAsset } from '../tradingView/util';
	import { marketsConfig } from '$lib/config/markets';
	import { isEntirePositionTPSLOrder } from '../util';
	import { LARGE_POSITIVE_INT } from '$lib/web3/contants';
	import type { Trade } from '$lib/types/trade';
	import { walletConnected, walletLoaded } from '$lib/wallet/store';
	import { smartContractWalletAddress } from '../scwallet/store';
	import { getAllOrderHistory, getAllTrades } from '$lib/data/userHoldings';
	import { writable, type Writable } from 'svelte/store';
	import ProcessingIcon from '$lib/util/ProcessingIcon.svelte';

	function getOrderStatus(ord: Order) {
		if (ord.status == 'FirstExecuted') {
			if (new BigNumber(ord.priceB).gte(new BigNumber(LARGE_POSITIVE_INT))) {
				if (new BigNumber(ord.size).gt(0)) {
					return 'Stop Executed';
				}
				return 'Limit Executed';
			}
			if (new BigNumber(ord.size).gt(0)) {
				return 'Limit Executed';
			}
			return 'Stop Executed';
		}
		if (ord.status == 'Cancelled' && ord.totalFee) {
			return 'TP/SL Cancelled';
		}
		if (ord.status == 'Completed' && ord.pairTotalFee && ord.pairExecutionType == 'TakeProfit') {
			return 'TP Executed';
		}
		if (ord.status == 'Completed' && ord.pairTotalFee && ord.pairExecutionType == 'StopLoss') {
			return 'SL Executed';
		}
		return ord.status;
	}

	function getFees(ord: Order) {
		let fee = '';
		if (ord.orderType == 'Limit' || ord.orderType == 'Advanced') {
			if (ord.totalFee) {
				fee += addDollarSign(getNumBackInUnits(ord.totalFee, 18, 4));
			} else if (ord.pairTotalFee) {
				fee += addDollarSign(getNumBackInUnits(ord.pairTotalFee, 18, 4));
			} else {
				fee += '-';
			}
		} else {
			fee += addDollarSign(
				getNumBackInUnits(
					new BigNumber(ord.commitDeposit)
						.plus(new BigNumber(ord.keeperDeposit))
						.plus(new BigNumber(ord.tradeFee))
						.toString(),
					18,
					4
				)
			);
		}
		return fee;
	}

	function getDateAndTime(timestamp: string) {
		return getDate(timestamp).replace(',', ' ') + ' ' + getTime(timestamp);
	}

	function getSide(trade: Trade) {
		if (trade.orderType == 'Liquidation') {
			return 'Liquidation';
		} else if (new BigNumber(trade.size).gt(0)) {
			return 'Long';
		} else {
			return 'Short';
		}
	}

	async function downloadOrderHistory(type: string) {
		loadingCSVData.set(true);
		let csvContent = 'data:text/csv;charset=utf-8,';
		const link = document.createElement('a');

		if (type == 'history') {
			link.setAttribute('download', 'history.csv');

			const header = [
				'Time',
				'Perp',
				'Side',
				'Size in units',
				'Size in dollars',
				'Execution Price',
				'Accrued Funding',
				'Realised PnL'
			];
			csvContent += header.join(',') + '\r\n';

			const allTrades = await getAllTrades();

			allTrades.forEach((trade) => {
				const time = getDateAndTime(trade.timestamp);
				const perp = getDisplayName(trade.marketIdentifier);
				const side = getSide(trade);
				const sizeInUnits =
					getNumBackInUnits(trade.size, 18, 4) + ' ' + getDisplayAsset(trade.asset);
				const sizeInDollars = addDollarSign(
					getNumBackInUnits(
						new BigNumber(trade.size).multipliedBy(trade.price).div(1e18).toString(),
						18,
						2
					)
				).replace(',', '');
				const executionPrice = addDollarSign(
					getNumBackInUnits(trade.price, 18, marketsConfig[trade.asset].showDecimals),
					marketsConfig[trade.asset].showDecimals
				).replace(',', '');

				const accruedFunding = trade.accruedFunding
					? addDollarSign(getNumBackInUnits(trade.accruedFunding, 18, 2)).replace(',', '')
					: '-';

				const realisedPnL = addDollarSign(getNumBackInUnits(trade.pnl, 18, 2)).replace(',', '');

				const row = [
					time,
					perp,
					side,
					sizeInUnits,
					sizeInDollars,
					executionPrice,
					accruedFunding,
					realisedPnL
				];
				csvContent += row.join(',') + '\r\n';
			});
		} else if (type == 'order history') {
			link.setAttribute('download', 'order-history.csv');
			const header = ['Time', 'Perp', 'Side', 'Size/Amount', 'Order Type', 'Status', 'Fees'];
			csvContent += header.join(',') + '\r\n';

			const data = await getAllOrderHistory();

			data.forEach((order) => {
				const time = getDateAndTime(order.timestamp);
				const perp = getDisplayName(order.marketIdentifier);
				const side = new BigNumber(order.size).gt(0) ? 'Long' : 'Short';
				const size = isEntirePositionTPSLOrder(order)
					? 'Entire Position'
					: getNumBackInUnits(order.size, 18, 4) + ' ' + getDisplayAsset(order?.asset);

				const orderType = order.orderType;

				const status = getOrderStatus(order);
				const fees = getFees(order);

				const row = [time, perp, side, size, orderType, status, fees];
				csvContent += row.join(',') + '\r\n';
			});
		}
		loadingCSVData.set(false);
		// download csv
		const encodedUri = encodeURI(csvContent);
		link.setAttribute('href', encodedUri);
		document.body.appendChild(link); // Required for FF
		link.click();
	}

	const loadingCSVData: Writable<boolean> = writable(false);
</script>

{#if !($walletLoaded && $walletConnected && $smartContractWalletAddress)}
	<div />
{:else if ($userHoldingActiveItem == 'order history' || $userHoldingActiveItem == 'history') && !$loadingCSVData}
	<button
		class="bg-fx-gradient-8 px-4 py-1 text-center text-fx-grey-300 text-sm cursor-pointer rounded-lg flex gap-2 items-center border border-fx-grey-700"
		on:click={(e) => {
			downloadOrderHistory($userHoldingActiveItem);
		}}
	>
		<img class="w-4" loading="lazy" src={'download.svg'} alt="download csv" />

		Download CSV
	</button>
{:else if ($userHoldingActiveItem == 'order history' || $userHoldingActiveItem == 'history') && $loadingCSVData}
	<button
		class="bg-fx-gradient-8 px-4 py-1 text-center text-fx-grey-400 text-sm cursor-pointer rounded-lg flex gap-2 items-center border border-fx-grey-700"
	>
		Downloading...
		<ProcessingIcon />
	</button>
{/if}
